import type React from 'react';

import { Card, CardBody, SocialIcon, Text } from '@change/ds-core-ui';
import { FacebookIcon } from '@change/ds-icons/FacebookIcon';
import { InstagramIcon } from '@change/ds-icons/InstagramIcon';
import { RedditIcon } from '@change/ds-icons/RedditIcon';
import { ShareIcon } from '@change/ds-icons/ShareIcon';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { WhatsAppIcon } from '@change/ds-icons/WhatsAppIcon';
import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import SHARE_FIRST_SOCIAL_STARTER_PLACEHOLDERS from '#config/i18n/placeholders/psf/promoteOrShare/share-first-social-starter.yml';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';

import { ShareButton } from './components/ShareButton';

/**
 * PageBody only mounts this card when psfShareFirstSocialStarterExperiment's variation isn't 'control' (see
 * usePageBody's `shareFirst` gate), so by the time this component renders it's always one of these two.
 */
type Variation = 'variant_1' | 'variant_2';

// variant_1 has no card sub-heading (see Figma) - only variant_2's gradient card carries one.
const CARD_TITLE_KEY_BY_VARIATION: Partial<Record<Variation, TranslationKey>> = {
	variant_2: translationKey('corgi.psf.promote_or_share.share_first_social_starter.variant_2.card_title'),
};

const CARD_BODY_KEY_BY_VARIATION: Record<Variation, TranslationKey> = {
	variant_1: translationKey('corgi.psf.promote_or_share.share_first_social_starter.variant_1.card_body'),
	variant_2: translationKey('corgi.psf.promote_or_share.share_first_social_starter.variant_2.card_body'),
};

const START_DELAY_S = 1;
const TIKTOK_JUMP_DURATION_MS = 950;
const POP_IN_STAGGER_S = 0.15;
const REVEAL_START_S = START_DELAY_S + TIKTOK_JUMP_DURATION_MS / 1000;

// SocialIcon re-sizes the icon itself, so each svg is sized from the container. TikTok and WhatsApp
// match the homepage's 16px source badges; the other three sit at 18px so all five look the same size.
const SOCIAL_ICONS = [
	{
		icon: <TikTokIcon />,
		variant: 'tiktok' as const,
		glyphClassName: '[&_svg]:size-4',
		animationClassName: 'animate-share-first-icon-tiktok-jump',
		delay: START_DELAY_S,
	},
	{
		icon: <InstagramIcon />,
		variant: 'instagram' as const,
		glyphClassName: '[&_svg]:size-4.5',
		animationClassName: 'animate-share-first-icon-pop-in',
		delay: REVEAL_START_S,
	},
	{
		icon: <RedditIcon />,
		variant: 'reddit' as const,
		glyphClassName: '[&_svg]:size-4.5',
		animationClassName: 'animate-share-first-icon-pop-in',
		delay: REVEAL_START_S + POP_IN_STAGGER_S,
	},
	{
		icon: <WhatsAppIcon />,
		variant: 'whatsapp' as const,
		glyphClassName: '[&_svg]:size-4',
		animationClassName: 'animate-share-first-icon-pop-in',
		delay: REVEAL_START_S + POP_IN_STAGGER_S * 2,
	},
	{
		icon: <FacebookIcon />,
		variant: 'facebook' as const,
		glyphClassName: '[&_svg]:size-4.75',
		animationClassName: 'animate-share-first-icon-pop-in',
		delay: REVEAL_START_S + POP_IN_STAGGER_S * 3,
	},
];

const ShareFirstSocialStarterCardBase: React.FC<EmptyProps> = () => {
	const { translate, typedTranslate } = useI18n();
	const {
		data: { psfShareFirstSocialStarterExperiment },
	} = usePsfPromoteOrSharePageData();
	const variation = psfShareFirstSocialStarterExperiment.variation as Variation;

	const shareButton = (
		<ShareButton
			variant={variation === 'variant_1' ? 'secondary' : 'primary'}
			label={translate('corgi.psf.promote_or_share.share_first_social_starter.share_button')}
			trailingIcon={variation === 'variant_1' ? <ShareIcon /> : undefined}
		/>
	);

	if (variation === 'variant_1') {
		return (
			<div className="mb-7 md:mb-9" data-testid="share-first-social-starter-card">
				<Text as="p" variant="body" className="m-0 mb-3 md:mb-4 md:text-lg">
					{typedTranslate(CARD_BODY_KEY_BY_VARIATION[variation])}
				</Text>
				{shareButton}
			</div>
		);
	}

	return (
		<Card
			className="border-stroke-weaker bg-gradient-background-light-2 mb-7 border"
			data-testid="share-first-social-starter-card"
		>
			<CardBody className="flex flex-col px-4!">
				<div className="mb-3 flex justify-center -space-x-1.5" data-testid="share-first-social-icons">
					{SOCIAL_ICONS.map(({ icon, variant, glyphClassName, animationClassName, delay }) => (
						<SocialIcon
							key={variant}
							icon={icon}
							size="medium"
							variant={variant}
							className={cn(
								'border-background-base elevation-low border-2',
								glyphClassName,
								animationClassName,
								'motion-reduce:animate-none',
							)}
							style={{ animationDelay: `${delay}s` }}
						/>
					))}
				</div>

				{CARD_TITLE_KEY_BY_VARIATION[variation] && (
					<Text as="h2" variant="heading" size="xx-small" className="m-0 mb-1">
						{typedTranslate(CARD_TITLE_KEY_BY_VARIATION[variation])}
					</Text>
				)}

				<Text as="p" variant="body" className="m-0 mb-3">
					{typedTranslate(CARD_BODY_KEY_BY_VARIATION[variation])}
				</Text>

				{shareButton}
			</CardBody>
		</Card>
	);
};

export const ShareFirstSocialStarterCard = withI18nPlaceholders(
	[SHARE_FIRST_SOCIAL_STARTER_PLACEHOLDERS],
	ShareFirstSocialStarterCardBase,
);
